/**
 * Kanban View Selector
 *
 * Dropdown selector for switching between Kanban views,
 * plus buttons for creating and managing views.
 */

import { useState, useCallback } from 'react';
import {
  Select,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Checkbox,
  Divider,
  Popconfirm,
  message,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  SettingOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  kanbanApi,
  type CardSettings,
  DEFAULT_CARD_SETTINGS,
} from '@api/kanban';

const { Text } = Typography;

interface KanbanViewSelectorProps {
  selectedViewId?: string;
  onViewSelect: (viewId: string) => void;
  entityType?: 'instances' | 'templates';
}

export default function KanbanViewSelector({
  selectedViewId,
  onViewSelect,
  entityType = 'instances',
}: KanbanViewSelectorProps) {
  const queryClient = useQueryClient();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();

  // Fetch views
  const { data: views = [], isLoading } = useQuery({
    queryKey: ['kanban-views'],
    queryFn: () => kanbanApi.listViews(),
  });

  // Filter views by entity type
  const filteredViews = views.filter(v => v.entityType === entityType);

  // Get selected view
  const selectedView = filteredViews.find(v => v.id === selectedViewId);

  // Create view mutation
  const createMutation = useMutation({
    mutationFn: kanbanApi.createView,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['kanban-views'] });
      // Initialize with default columns
      await kanbanApi.initializeDefaultColumns(data.id);
      onViewSelect(data.id);
      setCreateModalOpen(false);
      form.resetFields();
      message.success('View created');
    },
    onError: () => message.error('Failed to create view'),
  });

  // Update view mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => kanbanApi.updateView(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-views'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-view', selectedViewId] });
      setSettingsModalOpen(false);
      message.success('Settings saved');
    },
    onError: () => message.error('Failed to save settings'),
  });

  // Delete view mutation
  const deleteMutation = useMutation({
    mutationFn: kanbanApi.deleteView,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-views'] });
      // Select another view if available
      const remaining = filteredViews.filter(v => v.id !== selectedViewId);
      if (remaining.length > 0) {
        onViewSelect(remaining[0].id);
      }
      message.success('View deleted');
    },
    onError: () => message.error('Failed to delete view'),
  });

  // Handle create
  const handleCreate = useCallback((values: any) => {
    createMutation.mutate({
      name: values.name,
      description: values.description,
      entityType,
      cardSettings: DEFAULT_CARD_SETTINGS,
      isDefault: values.isDefault,
    });
  }, [createMutation, entityType]);

  // Handle settings save
  const handleSaveSettings = useCallback((values: any) => {
    if (!selectedViewId) return;

    const cardSettings: CardSettings = {
      showDescription: values.showDescription,
      showDueDate: values.showDueDate,
      showAssignee: values.showAssignee,
      showTags: values.showTags,
      showCategory: values.showCategory,
      showSubtasks: values.showSubtasks,
      showProgress: values.showProgress,
      showPriority: values.showPriority,
      cardSize: values.cardSize,
      descriptionLines: values.descriptionLines,
    };

    updateMutation.mutate({
      id: selectedViewId,
      data: {
        name: values.name,
        cardSettings,
        isDefault: values.isDefault,
      },
    });
  }, [selectedViewId, updateMutation]);

  // Open settings modal
  const openSettings = useCallback(() => {
    if (!selectedView) return;
    settingsForm.setFieldsValue({
      name: selectedView.name,
      isDefault: selectedView.isDefault,
      ...selectedView.cardSettings,
    });
    setSettingsModalOpen(true);
  }, [selectedView, settingsForm]);

  return (
    <>
      <Space>
        <Select
          value={selectedViewId}
          onChange={onViewSelect}
          loading={isLoading}
          style={{ width: 200 }}
          placeholder="Select view..."
          options={filteredViews.map(v => ({
            value: v.id,
            label: (
              <Space>
                {v.isDefault && <StarFilled style={{ color: 'var(--status-warning)', fontSize: 12 }} />}
                {v.name}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  ({v.columnCount || 0} cols)
                </Text>
              </Space>
            ),
          }))}
          popupRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ padding: '4px 8px' }}>
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  block
                  onClick={() => setCreateModalOpen(true)}
                >
                  Create New View
                </Button>
              </div>
            </>
          )}
        />
        {selectedViewId && (
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={openSettings}
          />
        )}
      </Space>

      {/* Create View Modal */}
      <Modal
        title="Create Kanban View"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="View Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="My Kanban View" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea placeholder="Optional description..." rows={2} />
          </Form.Item>
          <Form.Item name="isDefault" valuePropName="checked">
            <Checkbox>Set as default view</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Settings Modal */}
      <Modal
        title="View Settings"
        open={settingsModalOpen}
        onCancel={() => setSettingsModalOpen(false)}
        width={500}
        footer={[
          <Popconfirm
            key="delete"
            title="Delete this view?"
            description="This cannot be undone."
            onConfirm={() => selectedViewId && deleteMutation.mutate(selectedViewId)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
              Delete View
            </Button>
          </Popconfirm>,
          <Button key="cancel" onClick={() => setSettingsModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={() => settingsForm.submit()}
            loading={updateMutation.isPending}
          >
            Save Settings
          </Button>,
        ]}
      >
        <Form
          form={settingsForm}
          layout="vertical"
          onFinish={handleSaveSettings}
          initialValues={DEFAULT_CARD_SETTINGS}
        >
          <Form.Item name="name" label="View Name">
            <Input />
          </Form.Item>

          <Form.Item name="isDefault" valuePropName="checked">
            <Checkbox>
              <Space>
                <StarOutlined />
                Set as default view
              </Space>
            </Checkbox>
          </Form.Item>

          <Divider>Card Display Settings</Divider>

          <Form.Item name="cardSize" label="Card Size">
            <Select
              options={[
                { value: 'compact', label: 'Compact - Minimal info' },
                { value: 'normal', label: 'Normal - Balanced' },
                { value: 'detailed', label: 'Detailed - Full info' },
              ]}
            />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Form.Item name="showDescription" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Show Description</Checkbox>
            </Form.Item>
            <Form.Item name="showDueDate" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Show Due Date</Checkbox>
            </Form.Item>
            <Form.Item name="showAssignee" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Show Assignee</Checkbox>
            </Form.Item>
            <Form.Item name="showTags" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Show Tags</Checkbox>
            </Form.Item>
            <Form.Item name="showCategory" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Show Category</Checkbox>
            </Form.Item>
            <Form.Item name="showSubtasks" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Show Subtasks</Checkbox>
            </Form.Item>
            <Form.Item name="showProgress" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Show Progress Bar</Checkbox>
            </Form.Item>
            <Form.Item name="showPriority" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Show Priority</Checkbox>
            </Form.Item>
          </div>

          <Form.Item name="descriptionLines" label="Description Lines" style={{ marginTop: 16 }}>
            <Select
              options={[
                { value: 1, label: '1 line' },
                { value: 2, label: '2 lines' },
                { value: 3, label: '3 lines' },
                { value: 4, label: '4 lines' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
